import InputGrades from 'core/modules/atoms/InputGrades';
import InputPrintPoints from 'core/modules/atoms/InputPrintPoints';
import Numeration from 'core/modules/atoms/Numeration';
import { SelectCustomizable, Tag } from 'ui';
import StudentActivityAttachment from 'core/modules/atoms/StudentActivityAttachment/StudentActivityAttachment';
import { useLemonadeViewer } from 'core/modules/components/lemonade/hook/useLemonadeViewer';
import { useLocale } from 'core/hooks/useLocale';
import { formatDisplayScore } from 'core/utils/formatDisplayScore';
import { setStyle } from 'core/utils/lemonade';
import { parseGradesToScoreScale, parseGradesToStandardScale } from 'core/utils/parses';
import { roundNumber } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { withTranslation } from 'react-i18next';
import TagsList from '../../TagsList';
import TooltipLearningObjectives from '../../lessons/components/TooltipLearningObjectives';

const RESULT_TAG_PROPS = {
  correct: { color: 'success', labelKey: 'common:Correct', leftIcon: 'done-circle' },
  incorrect: { color: 'error', labelKey: 'common:Incorrect', leftIcon: 'incorrect-circle' },
  done: { color: 'blue', labelKey: 'common:Done', leftIcon: 'done-circle' },
  notDone: { color: 'error', labelKey: 'common:Not Done', leftIcon: 'incorrect-circle' },
};

const QuestionItem = ({
  t,
  question_guid,
  data,
  ranking_scale,
  number,
  title,
  limitWrongs,
  pointsTotal,
  onScoreChange,
  answer,
  score,
  showInput,
  isInstructionMode,
  isStudentResultsMode,
  isCorrectionMode,
  showSolutions,
  scoreScale,
  isTest,
  onOpenStudentAttachment,
  learningObjectives,
  isMintFeedback,
  questionDisabled,
  children,
  job,
  lang,
}) => {
  const locale = useLocale();

  const { Question, showCorrectAnswer, resetQuestion, showStudentResponse } = useLemonadeViewer({
    data: getData(data),
    ...(answer && { studentResponse: answer }),
    disableInteraction: true,
    jobStatus: job?.status,
  });


  const [selectedLearningObjectives, setSelectedLearningObjectives] = useState(learningObjectives);
  const [attachedFile, setAttachedFile] = useState([]);
  const [background, setBackground] = useState(null);

  useEffect(() => {
    if (data) {
      let newData = getData(data);
      setBackground(newData?.data?.ui_style?.background);
      setSelectedLearningObjectives(learningObjectives);
    }
  }, [data]);

  useEffect(() => {
    if (showSolutions) {
      showCorrectAnswer();
    } else {
      resetQuestion();

      if (answer) {
        setTimeout(() => {
          showStudentResponse(answer);
        }, 100);
      }
    }
  }, [showSolutions]);

  useEffect(() => {
    if (answer && answer[0]?.path) {
      try {
        let paths = answer.map((item) => item.path);
        setAttachedFile(paths);
      } catch (error) {
        console.error('error get attached file: ', error);
      }
    }
  }, [answer]);

  const deduceTag = () => {
    let isEvaluable = getData(data)?.data?.isEvaluable;
    if (isEvaluable) {
      return score === ranking_scale ? 'correct' : 'incorrect';
    } else {
      return answer ? 'done' : 'notDone';
    }
  };

  function renderResultTag() {
    const { color, labelKey, leftIcon } = RESULT_TAG_PROPS[deduceTag()];
    return <Tag color={color} label={t(labelKey)} leftIcon={leftIcon} shape="rounded" size="lg" />;
  }

  function renderScore() {
    if (!showInput) {
      return (
        <div className="question-item__ranking-scale">
          {formatDisplayScore(parseGradesToScoreScale(ranking_scale, scoreScale), locale)} {t('assigments:points')}
        </div>
      );
    }
    if (isStudentResultsMode) {
      // Hide score when job is delivered (awaiting teacher evaluation)
      if (job?.status === 'delivered') {
        return null;
      }

      let parsedScoreStudent;
      let parsedPointsTotal;
      let parseRankingScale = 10 / pointsTotal;
      if (scoreScale === 'A-F') {
        parsedScoreStudent = parseGradesToScoreScale(roundNumber(score) * parseRankingScale, scoreScale);
        parsedPointsTotal = parseGradesToScoreScale(10, scoreScale);
      } else {
        parsedScoreStudent = parseGradesToScoreScale(roundNumber(score), scoreScale);
        parsedPointsTotal = parseGradesToScoreScale(pointsTotal, scoreScale);
      }
      return (
        <div className="question-item__student-result-score">
          <span className="question-item__score">{formatDisplayScore(parsedScoreStudent, locale)}</span> <span className="question-item__total-points">/ {formatDisplayScore(parsedPointsTotal, locale)} </span>
          {scoreScale !== 'A-F' ? <span className="question-item__points-text">{pointsTotal > 1 ? t('assigments:points') : t('assigments:point')}</span> : ' '}
        </div>
      );
    }
    if (isInstructionMode) {
      return <InputPrintPoints size="medium" pointsTotal={parseGradesToScoreScale(scoreScale !== 'A-F' ? pointsTotal : 10, scoreScale)} />;
    } else {
      // TODO: Cambiar este if, porque ahora de 0 a 10 es igual que de 0 a 100
      if (scoreScale !== '0-10') {
        if (scoreScale === 'A-F') {
          let options = [
            { label: 'A', value: 'A' },
            { label: 'B', value: 'B' },
            { label: 'C', value: 'C' },
            { label: 'D', value: 'D' },
            { label: 'E', value: 'E' },
            { label: 'F', value: 'F' },
          ];
          let parseRankingScale = 10 / pointsTotal;
          return (
            <SelectCustomizable
              value={options.filter((grade) => grade.value === parseGradesToScoreScale(score * parseRankingScale, scoreScale))}
              size={'default'}
              isSearchable={false}
              isDisabled={questionDisabled}
              options={options}
              onChange={(newScore) => {
                onScoreChange({ score: Number(parseGradesToStandardScale(newScore.value, scoreScale) / parseRankingScale).toFixed(2), index: number - 1 });
              }}
            />
          );
        } else {
          return (
            <InputGrades
              disabled={questionDisabled}
              size="medium"
              placeholder="-"
              onChange={(newScore) => onScoreChange({ score: newScore, index: number - 1 })}
              pointsTotal={parseGradesToScoreScale(pointsTotal, scoreScale)}
              value={score}
              max={parseGradesToScoreScale(ranking_scale, scoreScale)}
              scoreScale={scoreScale}
              t={t}
            />
          );
        }
      } else {
        return (
          <InputGrades
            disabled={questionDisabled}
            size="medium"
            placeholder="-"
            onChange={(newScore) => onScoreChange({ score: newScore, index: number - 1 })}
            pointsTotal={parseGradesToScoreScale(pointsTotal, scoreScale)}
            value={score}
            max={parseGradesToScoreScale(ranking_scale, scoreScale)}
            scoreScale={scoreScale}
            t={t}
          />
        );
      }
    }
  }

  function getData(data) {
    let newData = data;
    try {
      newData = JSON.parse(data);
      if (!newData.data) newData = { type: newData.type, metadata: newData.metadata, reference: question_guid, data: newData };
    } catch {
      // Ignore JSON parse errors - data is already in correct format
    }

    return newData;
  }

  return (
    <div className="question-item">
      <div className="question-item__header">
        <div className="question-item__header-left">
          {isTest && (
            <div className="question-item__numeration">
              <Numeration number={number < 10 ? 0 + number : number} />
            </div>
          )}
          <div className="question-item__title">
            {/* lang solo en el contenido autor (WCAG 3.1.2); las etiquetas de UI
                (puntos, límite de palabras) quedan fuera para no cambiar su fonética. */}
            <span lang={lang}>{title}</span>
          </div>
        </div>
        <div className="question-item__header-right">
          {isTest && !isMintFeedback && <div className="question-item__points">{renderScore()}</div>}
          {isStudentResultsMode && job?.status !== 'delivered' && (
            <div className="question-item__result-tag">
              {renderResultTag()}
            </div>
          )}
        </div>
      </div>
      <div className="question-item__lemonade" lang={lang}>
        <div className="lemonade-view__container">
          {(isStudentResultsMode || isCorrectionMode) && attachedFile && attachedFile.length > 0 ? (
            attachedFile.map((item) => (
              <div className="question-item__attached-file" key={item}>
                <StudentActivityAttachment onOpenStudentAttachment={onOpenStudentAttachment} attachedFile={item} />
              </div>
            ))
          ) : (
            <div style={setStyle(background)}>
              <Question />
            </div>
          )}
          {!isStudentResultsMode && selectedLearningObjectives && selectedLearningObjectives.length > 0 && (
            <div className="lesson-item__tags">
              <TagsList
                colorTag="default"
                tags={selectedLearningObjectives.map((item) => ({
                  text: item.learning_objective.substring(0, 40),
                  tooltip: <TooltipLearningObjectives tooltipText={item.description} tooltipTitle={item.learning_objective} />,
                }))}
              />
            </div>
          )}
        </div>
      </div>
      {limitWrongs && (
        <div className="question-item__limit-wrong">
          <span className="question-item__limit-wrong-text">{t('Word limit', { length: 0, limit: limitWrongs })}</span>
        </div>
      )}
      {children}
    </div>
  );
};

QuestionItem.propTypes = {
  t: PropTypes.func,
  question_guid: PropTypes.string,
  data: PropTypes.object,
  ranking_scale: PropTypes.string,
  number: PropTypes.number,
  title: PropTypes.string,
  limitWrongs: PropTypes.number,
  pointsTotal: PropTypes.number,
  onScoreChange: PropTypes.func,
  answer: PropTypes.string,
  score: PropTypes.number,
  showInput: PropTypes.bool,
  isInstructionMode: PropTypes.bool,
  isStudentResultsMode: PropTypes.bool,
  showSolutions: PropTypes.bool,
  scoreScale: PropTypes.string,
  isTest: PropTypes.bool,
  isCorrectionMode: PropTypes.bool,
  isMintFeedback: PropTypes.bool,
  onOpenStudentAttachment: PropTypes.func,
  learningObjectives: PropTypes.array,
  lang: PropTypes.string,
  questionDisabled: PropTypes.bool,
  children: PropTypes.node,
  job: PropTypes.object,
};

QuestionItem.defaultProps = {
  pointsTotal: 0,
  showInput: true,
  isInstructionMode: false,
  isStudentResultsMode: false,
  isCorrectionMode: false,
  showSolutions: false,
  isTest: true,
  isMintFeedback: false,
  onOpenStudentAttachment: () => {},
  learningObjectives: [],
  children: null,
};

export default withTranslation(['assigments'])(QuestionItem);
